import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useContact } from '../context/ContactContext';
import { API_URL } from '../config/api';
import './HotelReservation.css';

// ── Data ────────────────────────────────────────────────────────────────────
const POPULAR_DESTINATIONS = [
  { city: 'New York', code: 'NYC', img: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=500&q=75', label: 'From $89/night' },
  { city: 'Las Vegas', code: 'LAS', img: 'https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=500&q=75', label: 'From $59/night' },
  { city: 'Miami', code: 'MIA', img: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=500&q=75', label: 'From $99/night' },
  { city: 'Chicago', code: 'CHI', img: 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=500&q=75', label: 'From $79/night' },
  { city: 'Cancún', code: 'CUN', img: 'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=500&q=75', label: 'From $129/night' },
  { city: 'Paris', code: 'PAR', img: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=500&q=75', label: 'From $149/night' },
  { city: 'London', code: 'LON', img: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=500&q=75', label: 'From $119/night' },
  { city: 'Dubai', code: 'DXB', img: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=500&q=75', label: 'From $139/night' },
];

const FEATURED_HOTELS = [
  { name: 'The Grand Beachfront Resort', city: 'Miami, FL', stars: 5, price: 249, img: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=75', amenities: ['Free WiFi', 'Pool', 'Spa', 'Beach Access'], rating: 4.9 },
  { name: 'Downtown Luxury Suites', city: 'New York, NY', stars: 4, price: 189, img: 'https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=75', amenities: ['Free WiFi', 'Gym', 'Restaurant', 'Rooftop Bar'], rating: 4.7 },
  { name: 'Desert Oasis Resort', city: 'Las Vegas, NV', stars: 5, price: 299, img: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?w=600&q=75', amenities: ['Free WiFi', 'Casino', 'Pool', 'Shows'], rating: 4.8 },
  { name: 'Lakeview Boutique Hotel', city: 'Chicago, IL', stars: 4, price: 159, img: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&q=75', amenities: ['Free WiFi', 'Restaurant', 'Bar', 'Concierge'], rating: 4.6 },
  { name: 'Riviera Palace Cancún', city: 'Cancún, Mexico', stars: 5, price: 329, img: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=75', amenities: ['All Inclusive', 'Beach', 'Pool', 'Water Sports'], rating: 4.9 },
  { name: 'Paris Heritage Hotel', city: 'Paris, France', stars: 4, price: 199, img: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=75', amenities: ['Free WiFi', 'Fine Dining', 'Spa', 'City Views'], rating: 4.7 },
];

const AMENITIES = [
  { icon: '📶', label: 'Free Wi-Fi', desc: 'High-speed internet in all rooms' },
  { icon: '🏊', label: 'Swimming Pool', desc: 'Indoor & outdoor pool options' },
  { icon: '🍳', label: 'Breakfast Included', desc: 'Free continental or full breakfast' },
  { icon: '🧖', label: 'Spa & Wellness', desc: 'Full-service spa & fitness center' },
  { icon: '🅿️', label: 'Free Parking', desc: 'On-site complimentary parking' },
  { icon: '🚌', label: 'Airport Shuttle', desc: 'Convenient hotel transfers' },
  { icon: '🍽️', label: 'Restaurant', desc: 'On-site dining & room service' },
  { icon: '🐾', label: 'Pet Friendly', desc: 'Welcoming hotels for your pets' },
];

const HOTEL_TYPES = [
  { icon: '🏨', label: 'Luxury Hotels', desc: '5-star resorts & premium stays' },
  { icon: '🌊', label: 'Beach Resorts', desc: 'Oceanfront relaxation & fun' },
  { icon: '🏙️', label: 'City Hotels', desc: 'Downtown & business district stays' },
  { icon: '🏡', label: 'Boutique Hotels', desc: 'Unique, intimate experiences' },
  { icon: '🌿', label: 'Eco Resorts', desc: 'Sustainable, nature-friendly stays' },
  { icon: '🎲', label: 'Casino Hotels', desc: 'Entertainment & gaming resorts' },
];

const FAQS = [
  { q: 'How do I book a hotel through MFD Holidays?', a: 'Search your destination, select dates and guests above, then browse available hotels. You can also call our 24/7 support line and our travel experts will handle everything for you.' },
  { q: 'Is there a fee to call for hotel booking?', a: 'No — calling our support line is completely free. Our agents will search, compare, and book the best hotel for you at no extra charge.' },
  { q: 'Can I get a better deal by calling vs. booking online?', a: 'Often yes! Our agents have access to exclusive rates, unpublished deals, and bundle discounts that may not be available on the website.' },
  { q: 'What is the cancellation policy for hotels?', a: 'Cancellation policies vary by hotel and rate type. Free cancellation is available on most flexible rates. Our team will clearly explain all terms before confirming your booking.' },
  { q: 'Do you offer hotel + flight packages?', a: 'Yes! Bundling flights and hotels can save you up to 30%. Call us to get a custom package quote for your trip.' },
  { q: 'Can I book for groups or business travel?', a: 'Absolutely. We specialize in group bookings and corporate travel. Call us for bulk room rates, meeting facilities, and negotiated corporate fares.' },
];

// ── Component ────────────────────────────────────────────────────────────────
export default function HotelReservation() {
  const { contactSettings } = useContact();
  const navigate = useNavigate();
  const tfn = '1-844-480-0252';
  const tfnClean = tfn.replace(/[^0-9+]/g, '');
  const today = new Date().toISOString().split('T')[0];

  const [destination, setDestination] = useState('');
  const [destCode, setDestCode] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [rooms, setRooms] = useState('1');
  const [adults, setAdults] = useState('2');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const destRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (destRef.current && !destRef.current.contains(e.target)) setSuggestions([]);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchCities = useCallback(async (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/flights/airports?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setSuggestions((data.data || []).slice(0, 8));
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDestChange = (val) => {
    setDestination(val);
    setDestCode('');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchCities(val), 300);
  };

  const selectDest = (s) => {
    setDestination(`${s.cityName} (${s.iataCode})`);
    setDestCode(s.iataCode);
    setSuggestions([]);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const code = destCode || destination.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    if (!code || !checkIn || !checkOut) return;
    navigate('/hotel-results', { state: { cityCode: code, checkInDate: checkIn, checkOutDate: checkOut, adults: parseInt(adults), roomQuantity: parseInt(rooms) } });
  };

  return (
    <div className="hr-page">
      <Helmet>
        <title>Hotel Reservations – Best Hotel Deals | MFD Holidays</title>
        <meta name="description" content="Book the best hotels at the lowest prices. Search 500,000+ hotels worldwide. Free cancellation on most bookings. Call our 24/7 hotel booking experts for exclusive deals." />
        <meta name="keywords" content="hotel reservations, book hotels online, cheap hotels, hotel deals, hotel booking, best hotel rates, luxury hotels, beach resorts, hotel near airport, MFD Holidays hotels" />
        <link rel="canonical" href="https://mfdholidays.ca/hotel/reservation" />
        <meta property="og:title" content="Hotel Reservations – Best Hotel Deals | MFD Holidays" />
        <meta property="og:description" content="Book 500,000+ hotels at the best prices. Free cancellation. 24/7 expert support." />
      </Helmet>

      {/* ── Hero ── */}
      <section className="hr-hero">
        <div className="hr-hero-overlay" />
        <div className="hr-hero-content">
          <div className="hr-hero-badge">🏨 500,000+ Hotels Worldwide</div>
          <h1>Find Your Perfect Hotel</h1>
          <p>Best rates guaranteed · Free cancellation on most bookings · 24/7 expert support</p>

          {/* ── Search Box ── */}
          <div className="hr-search-box">
            <form onSubmit={handleSearch}>
              <div className="hr-search-row">
                {/* Destination */}
                <div className="hr-field hr-dest-wrap" ref={destRef}>
                  <label>📍 Destination</label>
                  <input
                    type="text"
                    placeholder="City, hotel or landmark"
                    value={destination}
                    onChange={e => handleDestChange(e.target.value)}
                    autoComplete="off"
                    required
                  />
                  {loading && <div className="hr-ac-loading">Searching…</div>}
                  {suggestions.length > 0 && (
                    <ul className="hr-ac-dropdown">
                      {suggestions.map(s => (
                        <li key={s.iataCode} onMouseDown={() => selectDest(s)}>
                          <span className="hr-ac-code">{s.iataCode}</span>
                          <span className="hr-ac-name">{s.cityName}{s.countryName ? `, ${s.countryName}` : ''}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="hr-field">
                  <label>📅 Check-In</label>
                  <input type="date" min={today} value={checkIn} onChange={e => setCheckIn(e.target.value)} required />
                </div>
                <div className="hr-field">
                  <label>📅 Check-Out</label>
                  <input type="date" min={checkIn || today} value={checkOut} onChange={e => setCheckOut(e.target.value)} required />
                </div>
                <div className="hr-field hr-field-sm">
                  <label>👤 Guests</label>
                  <select value={adults} onChange={e => setAdults(e.target.value)}>
                    {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n} Guest{n>1?'s':''}</option>)}
                  </select>
                </div>
                <div className="hr-field hr-field-sm">
                  <label>🛏️ Rooms</label>
                  <select value={rooms} onChange={e => setRooms(e.target.value)}>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} Room{n>1?'s':''}</option>)}
                  </select>
                </div>
                <button type="submit" className="hr-search-btn">Search Hotels</button>
              </div>
            </form>
          </div>

          {/* Or Call */}
          <div className="hr-hero-call">
            <span>Or call our hotel experts:</span>
            <a href={`tel:${tfnClean}`} className="hr-hero-phone">{tfn}</a>
          </div>
        </div>
      </section>

      {/* ── Popular Destinations ── */}
      <section className="hr-section hr-destinations">
        <div className="hr-inner">
          <h2 className="hr-section-title">Popular Hotel Destinations</h2>
          <p className="hr-section-sub">Top-rated destinations with the best hotel deals</p>
          <div className="hr-dest-grid">
            {POPULAR_DESTINATIONS.map(d => (
              <a key={d.code} href="#" onClick={e => { e.preventDefault(); setDestination(`${d.city} (${d.code})`); setDestCode(d.code); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="hr-dest-card">
                <img src={d.img} alt={d.city} loading="lazy" />
                <div className="hr-dest-info">
                  <span className="hr-dest-city">{d.city}</span>
                  <span className="hr-dest-price">{d.label}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Featured Hotels ── */}
      <section className="hr-section hr-featured">
        <div className="hr-inner">
          <h2 className="hr-section-title">Featured Hotels</h2>
          <p className="hr-section-sub">Handpicked top-rated properties at exclusive rates</p>
          <div className="hr-hotels-grid">
            {FEATURED_HOTELS.map(h => (
              <div key={h.name} className="hr-hotel-card">
                <div className="hr-hotel-img-wrap">
                  <img src={h.img} alt={h.name} loading="lazy" />
                  <div className="hr-hotel-stars">{'⭐'.repeat(h.stars)}</div>
                </div>
                <div className="hr-hotel-body">
                  <h3>{h.name}</h3>
                  <p className="hr-hotel-city">📍 {h.city}</p>
                  <div className="hr-hotel-amenities">
                    {h.amenities.map(a => <span key={a} className="hr-amenity-tag">{a}</span>)}
                  </div>
                  <div className="hr-hotel-footer">
                    <div>
                      <span className="hr-hotel-price">${h.price}</span>
                      <span className="hr-hotel-per"> / night</span>
                    </div>
                    <div className="hr-hotel-rating">⭐ {h.rating}</div>
                  </div>
                  <a href={`tel:${tfnClean}`} className="hr-book-btn">📞 Book Now — {tfn}</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Hotel Types ── */}
      <section className="hr-section hr-types">
        <div className="hr-inner">
          <h2 className="hr-section-title">Browse by Hotel Type</h2>
          <div className="hr-types-grid">
            {HOTEL_TYPES.map(t => (
              <a key={t.label} href={`tel:${tfnClean}`} className="hr-type-card">
                <div className="hr-type-icon">{t.icon}</div>
                <h4>{t.label}</h4>
                <p>{t.desc}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Amenities ── */}
      <section className="hr-section hr-amenities-sec">
        <div className="hr-inner">
          <h2 className="hr-section-title">Top Hotel Amenities</h2>
          <div className="hr-amenities-grid">
            {AMENITIES.map(a => (
              <div key={a.label} className="hr-amenity-card">
                <div className="hr-amenity-icon">{a.icon}</div>
                <h4>{a.label}</h4>
                <p>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Customer Support Cards ── */}
      <section className="hr-section hr-support">
        <div className="hr-inner">
          <h2 className="hr-section-title">Need Help Booking Your Hotel?</h2>
          <p className="hr-section-sub">Our travel experts are available 24/7 to find you the best rates</p>
          <div className="hr-support-grid">
            <div className="hr-support-card" onClick={() => window.location.href = `tel:${tfnClean}`}>
              <div className="hr-support-img">
                <img src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=75" alt="Hotel booking expert" loading="lazy" />
                <div className="hr-support-overlay"><span>📞 Call Now</span></div>
              </div>
              <div className="hr-support-body">
                <h3>Hotel Search & Booking</h3>
                <p>Find and book the perfect hotel — we compare thousands of properties and negotiate exclusive rates.</p>
                <a href={`tel:${tfnClean}`} className="hr-support-btn" onClick={e => e.stopPropagation()}>📞 {tfn}</a>
              </div>
            </div>
            <div className="hr-support-card" onClick={() => window.location.href = `tel:${tfnClean}`}>
              <div className="hr-support-img">
                <img src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=75" alt="Group travel specialist" loading="lazy" />
                <div className="hr-support-overlay"><span>📞 Call Now</span></div>
              </div>
              <div className="hr-support-body">
                <h3>Group & Business Travel</h3>
                <p>Planning for multiple rooms or a corporate trip? We offer group rates and dedicated support.</p>
                <a href={`tel:${tfnClean}`} className="hr-support-btn" onClick={e => e.stopPropagation()}>📞 {tfn}</a>
              </div>
            </div>
            <div className="hr-support-card" onClick={() => window.location.href = `tel:${tfnClean}`}>
              <div className="hr-support-img">
                <img src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=400&q=75" alt="Hotel plus flight agent" loading="lazy" />
                <div className="hr-support-overlay"><span>📞 Call Now</span></div>
              </div>
              <div className="hr-support-body">
                <h3>Hotel + Flight Bundle</h3>
                <p>Save up to 30% by bundling your hotel with flights. Our agents build custom packages for your trip.</p>
                <a href={`tel:${tfnClean}`} className="hr-support-btn" onClick={e => e.stopPropagation()}>📞 {tfn}</a>
              </div>
            </div>
            <div className="hr-support-card" onClick={() => window.location.href = `tel:${tfnClean}`}>
              <div className="hr-support-img">
                <img src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&q=75" alt="Cancellation and changes agent" loading="lazy" />
                <div className="hr-support-overlay"><span>📞 Call Now</span></div>
              </div>
              <div className="hr-support-body">
                <h3>Changes & Cancellations</h3>
                <p>Need to modify or cancel your hotel booking? We handle it fast with minimal hassle.</p>
                <a href={`tel:${tfnClean}`} className="hr-support-btn" onClick={e => e.stopPropagation()}>📞 {tfn}</a>
              </div>
            </div>
          </div>
          {/* Big CTA */}
          <div className="hr-support-cta">
            <div className="hr-support-cta-icon">📞</div>
            <div className="hr-support-cta-text">
              <strong>Speak with a Hotel Booking Expert Now</strong>
              <span>No hold times · Exclusive rates · Available 24/7</span>
            </div>
            <a href={`tel:${tfnClean}`} className="hr-support-cta-btn">{tfn}</a>
          </div>
        </div>
      </section>

      {/* ── Why Book With Us ── */}
      <section className="hr-section hr-why">
        <div className="hr-inner">
          <h2 className="hr-section-title">Why Book Hotels With MFD Holidays?</h2>
          <div className="hr-why-grid">
            <div className="hr-why-card">
              <div className="hr-why-icon">💰</div>
              <h4>Best Price Guarantee</h4>
              <p>We match or beat any lower hotel rate you find. Book with confidence.</p>
            </div>
            <div className="hr-why-card">
              <div className="hr-why-icon">🔓</div>
              <h4>Free Cancellation</h4>
              <p>Most hotels offer free cancellation. Change your plans without penalty.</p>
            </div>
            <div className="hr-why-card">
              <div className="hr-why-icon">📞</div>
              <h4>24/7 Expert Support</h4>
              <p>Real agents available around the clock for bookings, changes and help.</p>
            </div>
            <div className="hr-why-card">
              <div className="hr-why-icon">🏨</div>
              <h4>500,000+ Hotels</h4>
              <p>Choose from the world's largest hotel inventory in 200+ countries.</p>
            </div>
            <div className="hr-why-card">
              <div className="hr-why-icon">🎁</div>
              <h4>Exclusive Deals</h4>
              <p>Access member-only rates and bundle discounts not available elsewhere.</p>
            </div>
            <div className="hr-why-card">
              <div className="hr-why-icon">⚡</div>
              <h4>Instant Confirmation</h4>
              <p>Get your booking confirmed instantly with email & SMS notification.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="hr-section hr-faq">
        <div className="hr-inner hr-inner-narrow">
          <h2 className="hr-section-title">Frequently Asked Questions</h2>
          <div className="hr-faq-list">
            {FAQS.map((f, i) => (
              <div key={i} className={`hr-faq-item ${openFaq === i ? 'open' : ''}`}>
                <button className="hr-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{f.q}</span>
                  <span className="hr-faq-chevron">{openFaq === i ? '▲' : '▼'}</span>
                </button>
                {openFaq === i && <div className="hr-faq-a">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="hr-final-cta">
        <div className="hr-inner">
          <div className="hr-cta-box">
            <div className="hr-cta-icon">🏨</div>
            <div className="hr-cta-text">
              <h2>Ready to Book Your Hotel?</h2>
              <p>Let our travel experts find you the perfect hotel at the best possible rate — in minutes.</p>
              <ul className="hr-cta-bullets">
                <li>✔ No booking fees · No hidden charges</li>
                <li>✔ Access to 500,000+ hotels worldwide</li>
                <li>✔ Free cancellation on most rates</li>
                <li>✔ Speak to a live agent instantly</li>
              </ul>
            </div>
            <div className="hr-cta-phone">
              <a href={`tel:${tfnClean}`} className="hr-big-call">
                <span className="hr-big-call-label">Call to Book Your Hotel</span>
                <span className="hr-big-call-num">{tfn}</span>
              </a>
              <p className="hr-cta-avail">Available 24 hours · 7 days a week</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Floating Call Button ── */}
      <a href={`tel:${tfnClean}`} className="hr-float-call" aria-label="Call now">
        <span className="hr-float-icon">📞</span>
        <span className="hr-float-text">
          <span className="hr-float-label">Book Hotel — 24/7</span>
          <span className="hr-float-num">{tfn}</span>
        </span>
      </a>
    </div>
  );
}
